/**
 * PasswordlessStatusIndicator Component
 *
 * Large status indicators showing passwordless readiness counts.
 * Fills the empty space at the top-right of the header card.
 */

import { useState } from 'react';
import { Text, makeStyles, tokens, shorthands, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Button } from '@fluentui/react-components';
import { CheckmarkCircle20Regular, ErrorCircle20Regular, Info24Regular } from '@fluentui/react-icons';

interface PasswordlessStatusIndicatorProps {
  readyCount: number;
  notReadyCount: number;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalXL),
    alignItems: 'center',
  },
  indicator: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  iconReady: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '16px',
  },
  iconNotReady: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '16px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'row',
    ...shorthands.gap(tokens.spacingHorizontalXS),
    alignItems: 'baseline',
  },
  countReady: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteGreenForeground1,
    lineHeight: tokens.lineHeightBase200,
  },
  countNotReady: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
    lineHeight: tokens.lineHeightBase200,
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  labelWithIcon: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXXS),
  },
  infoIcon: {
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    ':hover': {
      color: tokens.colorBrandForeground1,
    },
  },
});

export function PasswordlessStatusIndicator({
  readyCount,
  notReadyCount,
}: PasswordlessStatusIndicatorProps) {
  const styles = useStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <div className={styles.container}>
        {/* Ready Users */}
        <div className={styles.indicator}>
          <CheckmarkCircle20Regular className={styles.iconReady} />
          <div className={styles.statContent}>
            <Text className={styles.countReady} data-testid="ready-count">{readyCount}</Text>
            <Text className={styles.label}>Passwordless Ready</Text>
          </div>
        </div>

        {/* Not Ready Users */}
        <div className={styles.indicator}>
          <ErrorCircle20Regular className={styles.iconNotReady} />
          <div className={styles.statContent}>
            <Text className={styles.countNotReady} data-testid="not-ready-count">{notReadyCount}</Text>
            <Text className={styles.label}>Not Ready</Text>
          </div>
        </div>

        {/* Info Icon */}
        <Info24Regular
          className={styles.infoIcon}
          onClick={() => setIsDialogOpen(true)}
          data-testid="info-icon"
          aria-label="More information about passwordless readiness"
        />
      </div>

      {/* Info Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(_event, data) => setIsDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle style={{ textAlign: 'center', marginBottom: '24px' }}>Passwordless Readiness</DialogTitle>
            <DialogContent>
              <div>
                <Text as="p" style={{ marginBottom: tokens.spacingVerticalM }}>
                  <strong>Passwordless Ready:</strong> For a user account to be considered passwordless ready, it must have at least one of the following high-security authentication methods registered:
                </Text>
                <ul style={{ margin: 0, paddingLeft: '24px', lineHeight: '1.6' }}>
                  <li style={{ marginBottom: tokens.spacingVerticalXS }}>FIDO2 Security Keys (Physical hardware tokens like YubiKey)</li>
                  <li style={{ marginBottom: tokens.spacingVerticalXS }}>Windows Hello for Business (Biometric or PIN authentication)</li>
                  <li style={{ marginBottom: tokens.spacingVerticalXS }}>Microsoft Authenticator (Push notifications)</li>
                  <li>X.509 Certificate (Smart cards or digital certificates)</li>
                </ul>
              </div>
              <div style={{ marginTop: '32px' }}>
                <Text as="p">
                  <strong>Not Ready:</strong> This indicates the user has none of these high-security methods registered and will be prevented from logging in if the Passwordless Enforcement CA policy is enabled.
                </Text>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setIsDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}
